'use client';

import { useState, useRef } from 'react';
import { PortableTextWrapper } from '@/components/PortableTextWrapper';
import { PortableTextBlock } from '@sanity/types';
import type { ArbitraryTypedObject } from '@portabletext/types';

export type KeyFeature = {
  title: string;
  content:
    | PortableTextBlock
    | ArbitraryTypedObject
    | (PortableTextBlock | ArbitraryTypedObject)[];
};

type KeyFeaturesProps = {
  title?: string;
  keyFeatures?: KeyFeature[];
};

const AccordionItem = ({
  feature,
  isOpen,
  onToggle,
}: {
  feature: KeyFeature;
  isOpen: boolean;
  onToggle: () => void;
}) => {
  const contentRef = useRef<HTMLDivElement>(null);
  const { title, content } = feature;

  return (
    <li>
      <button
        className="relative flex items-center w-full pt-8 pb-2 text-base text-left"
        onClick={onToggle}
        aria-expanded={isOpen}
      >
        <span
          className={`flex-0 text-base-content ${
            isOpen ? 'text-black font-medium' : 'text-gray-600'
          }`}
        >
          <h6
            className={`relative w-auto ${isOpen ? 'text-black font-medium' : 'text-gray-600'}`}
          >
            {title}
            <span
              className={`absolute transform -translate-x-1/2 bottom-[-16px] w-0 h-0 
              border-l-[8px] border-l-transparent border-r-[8px] border-r-transparent border-t-[8px] 
              ${
                isOpen
                  ? 'right-[-8px] border-t-primary'
                  : 'left-2 border-t-gray-300'
              }`}
            />
          </h6>
        </span>
        <div
          className={`absolute left-0 right-0 bottom-0 h-[1px] ${isOpen ? 'bg-primary' : 'bg-gray-300'} rounded`}
        />
      </button>

      <div
        ref={contentRef}
        className="transition-all duration-300 ease-in-out text-base-content-secondary overflow-hidden"
        style={{ maxHeight: isOpen ? contentRef.current?.scrollHeight : 0 }}
      >
        <div className="leading-relaxed bg-lightGray px-4 lg:px-8 pt-6 pb-1">
          {content && <PortableTextWrapper value={content} />}
        </div>
      </div>
    </li>
  );
};

const KeyFeatures = ({ title, keyFeatures = [] }: KeyFeaturesProps) => {
  const [openFeatureIndex, setOpenFeatureIndex] = useState(0);

  return (
    <div className="mt-16" id="features">
      {title && <h5 className="font-medium text-xl mb-4">{title}</h5>}
      <div className="flex flex-col md:flex-row gap-12 md:gap-24">
        <ul className="w-full">
          {keyFeatures.map((feature, index) => (
            <AccordionItem
              key={feature.title}
              feature={feature}
              isOpen={openFeatureIndex === index}
              onToggle={() => setOpenFeatureIndex(index)}
            />
          ))}
        </ul>
      </div>
    </div>
  );
};

export default KeyFeatures;
